import { z } from 'zod';

/**
 * Runtime schemas for everything crossing the browser ↔ backend boundary.
 * Responses are parsed on the client; requests are re-validated on the server
 * (Edge Functions with these schemas, SQL functions with their own checks).
 */

const iso = z.string().min(1);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const uuid = z.uuid();
const nullableNumber = z.number().nullable();

export const clefSchema = z.enum(['treble', 'bass', 'alto', 'tenor']);
export const accidentalSchema = z.enum(['natural', 'sharp', 'flat']);
export const answerModeSchema = z.enum(['letter', 'spelled', 'spelled-octave']);
export const pitchIdSchema = z.string().regex(/^[A-G](#|b)?[0-8]$/);

/** Shape of a note-identification config. Musical rules live in the domain validator. */
export const noteIdConfigSchema = z.object({
  clef: clefSchema,
  grandStaff: z.boolean().optional(),
  range: z.object({ low: pitchIdSchema, high: pitchIdSchema }),
  accidentals: z.array(accidentalSchema).min(1),
  answerMode: answerModeSchema,
  questionCount: z.number().int(),
  staffFilter: z.enum(['all', 'lines', 'spaces']).default('all'),
  pool: z.array(pitchIdSchema).optional(),
  accidentalRate: z.number().min(0).max(1).default(0.5),
  includeRareSpellings: z.boolean().default(false),
  hintsAllowed: z.boolean().default(true),
});

export const exerciseTypeSchema = z.enum(['note-identification', 'rhythm']);
export type ExerciseTypeName = z.infer<typeof exerciseTypeSchema>;

/** Shape of a rhythm config. Musical rules live in the domain validator. */
export const rhythmConfigSchema = z.object({
  timeSignature: z.string(),
  tempo: z.number().int(),
  measures: z.union([z.literal(1), z.literal(2)]),
  noteValues: z.array(z.enum(['whole', 'half', 'quarter', 'eighth', 'sixteenth'])).min(1),
  dotted: z.boolean(),
  rests: z.boolean(),
  ties: z.boolean(),
  /** Absent in configs saved before triplets existed (treated as off). */
  triplets: z.boolean().optional(),
  questionCount: z.number().int(),
  /** Optional (engine v4): beat grouping, counting compound meters in eighths, strong-beats practice. */
  grouping: z.array(z.number().int()).optional(),
  countUnit: z.enum(['beat', 'eighth']).optional(),
  mode: z.enum(['rhythm', 'strong-beats']).optional(),
});

/** Either family's stored config; read it together with the row's `exerciseType`. */
export const exerciseConfigSchema = z.union([noteIdConfigSchema, rhythmConfigSchema]);

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export const instrumentProfileSchema = z.object({
  id: uuid,
  instrumentKey: z.string(),
  label: z.string(),
  primaryClef: clefSchema,
  rangeLow: pitchIdSchema,
  rangeHigh: pitchIdSchema,
  transpositionSemitones: z.number().int(),
  isActive: z.boolean(),
});
export type InstrumentProfile = z.infer<typeof instrumentProfileSchema>;

export const profileSchema = z.object({
  id: uuid,
  displayName: z.string(),
  timezone: z.string(),
  wantsStudent: z.boolean(),
  wantsTeacher: z.boolean(),
  activeWorkspace: z.enum(['student', 'teacher']),
  experienceLevel: z.enum(['new', 'some', 'confident']).nullable(),
  dailyGoalMinutes: z.number().int(),
  soundEnabled: z.boolean(),
  reducedMotion: z.enum(['system', 'reduce', 'no-preference']),
  leaderboardOptOut: z.boolean(),
  /** Show my display name on the app-wide leaderboard (opt-in). Defaults while the server catches up. */
  globalLeaderboardShowName: z.boolean().default(false),
  inAppAssignmentNotices: z.boolean(),
  onboardedAt: iso.nullable(),
});
export type Profile = z.infer<typeof profileSchema>;

export const meSchema = z.object({
  profile: profileSchema,
  instruments: z.array(instrumentProfileSchema),
  ownsClassrooms: z.boolean(),
  activeClassCount: z.number().int(),
  unreadNotifications: z.number().int(),
});
export type Me = z.infer<typeof meSchema>;

export const instrumentInputSchema = z.object({
  instrumentKey: z.string().regex(/^[a-z0-9-]{1,40}$/),
  label: z.string().trim().min(1).max(60),
  primaryClef: clefSchema,
  rangeLow: pitchIdSchema,
  rangeHigh: pitchIdSchema,
  transpositionSemitones: z.number().int().min(-24).max(24),
});
export type InstrumentInput = z.infer<typeof instrumentInputSchema>;

export const onboardingInputSchema = z.object({
  displayName: z.string().trim().min(1, 'Enter a display name.').max(40, 'Use 40 characters or fewer.'),
  timezone: z.string().min(1),
  wantsStudent: z.boolean(),
  wantsTeacher: z.boolean(),
  experienceLevel: z.enum(['new', 'some', 'confident']),
  dailyGoalMinutes: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]),
  instrument: instrumentInputSchema.nullable(),
});
export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

export const profilePatchSchema = z
  .object({
    display_name: z.string().trim().min(1).max(40),
    timezone: z.string(),
    wants_student: z.boolean(),
    wants_teacher: z.boolean(),
    active_workspace: z.enum(['student', 'teacher']),
    daily_goal_minutes: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]),
    sound_enabled: z.boolean(),
    reduced_motion: z.enum(['system', 'reduce', 'no-preference']),
    leaderboard_opt_out: z.boolean(),
    global_leaderboard_show_name: z.boolean(),
    in_app_assignment_notices: z.boolean(),
  })
  .partial();
export type ProfilePatch = z.infer<typeof profilePatchSchema>;

// ---------------------------------------------------------------------------
// Student
// ---------------------------------------------------------------------------

export const trackSchema = z.object({
  clef: clefSchema,
  rangeLow: pitchIdSchema,
  rangeHigh: pitchIdSchema,
  label: z.string(),
  instrumentProfileId: uuid,
  transpositionSemitones: z.number().int(),
});
export type Track = z.infer<typeof trackSchema>;

export const lessonProgressSchema = z.object({
  lessonId: z.string(),
  status: z.enum(['completed', 'placed']),
  bestAccuracy: nullableNumber,
  completions: z.number().int(),
  lastCompletedAt: iso.nullable().optional(),
});

export const placementSchema = z.object({
  method: z.enum(['assessment', 'start_from_beginning']),
  startLessonId: z.string(),
  createdAt: iso,
  placedLessonIds: z.array(z.string()),
  stageResults: z.array(z.object({ stageId: z.string(), correct: z.number(), total: z.number(), passed: z.boolean() })),
});

export const studentAssignmentSchema = z.object({
  assignmentId: uuid,
  title: z.string(),
  classroomId: uuid,
  classroomName: z.string(),
  releaseAt: iso,
  dueAt: iso.nullable(),
  requiredSessions: z.number().int(),
  masteryTarget: nullableNumber,
  assignmentFocused: z.boolean(),
  questionCount: z.number().int(),
  exerciseType: exerciseTypeSchema.default('note-identification'),
  config: exerciseConfigSchema,
  sessionsCompleted: z.number().int(),
  completedAt: iso.nullable(),
  completedLate: z.boolean().nullable(),
  masteryMet: z.boolean(),
  firstAttemptAccuracy: nullableNumber,
});
export type StudentAssignment = z.infer<typeof studentAssignmentSchema>;

export const activeSessionSummarySchema = z.object({
  id: uuid,
  kind: z.enum(['lesson', 'assignment', 'practice', 'review', 'placement']),
  title: z.string(),
  lessonId: z.string().nullable(),
  assignmentId: uuid.nullable(),
  startedAt: iso,
  expiresAt: iso,
  questionCount: z.number().int(),
  answered: z.number().int(),
});

export const notificationSchema = z.object({
  id: uuid,
  kind: z.enum(['assignment_published', 'assignment_updated', 'removed_from_class']),
  payload: z.record(z.string(), z.unknown()),
  createdAt: iso,
});

export const studentHomeSchema = z.object({
  today: isoDate,
  serverTime: iso,
  timezone: z.string(),
  goalMinutes: z.number().int(),
  minutesToday: z.number().int(),
  totalXp: z.number().int(),
  currentStreak: z.number().int(),
  longestStreak: z.number().int(),
  practicedToday: z.boolean(),
  track: trackSchema.nullable(),
  lessonProgress: z.array(lessonProgressSchema),
  /** Progress on the rhythm path (one track, whatever the instrument). Defaults while the server catches up. */
  rhythmLessonProgress: z.array(lessonProgressSchema).default([]),
  placement: placementSchema.nullable(),
  dueReviewSkillIds: z.array(z.string()),
  assignments: z.array(studentAssignmentSchema),
  upcomingCount: z.number().int(),
  activeSessions: z.array(activeSessionSummarySchema),
  notifications: z.array(notificationSchema),
});
export type StudentHome = z.infer<typeof studentHomeSchema>;

/** A path track: a clef (note reading) or "rhythm". */
export const trackKeySchema = z.union([clefSchema, z.literal('rhythm')]);

export const pathProgressSchema = z.object({
  clef: trackKeySchema,
  lessons: z.array(lessonProgressSchema),
  placement: placementSchema.nullable(),
  versions: z.record(z.string(), z.number()),
});
export type PathProgress = z.infer<typeof pathProgressSchema>;

export const skillStateSchema = z.object({
  skillId: z.string(),
  status: z.enum(['new', 'learning', 'practiced', 'mastered']),
  windowCount: z.number().int(),
  windowCorrect: z.number().int(),
  windowSessions: z.number().int(),
  reviewStep: z.number().int(),
  nextReviewOn: isoDate.nullable(),
  lastEvidenceAt: iso.nullable(),
  masteredAt: iso.nullable(),
});
export const skillProgressSchema = z.array(skillStateSchema);
export type SkillState = z.infer<typeof skillStateSchema>;

export const historyItemSchema = z.object({
  id: uuid,
  kind: z.enum(['lesson', 'assignment', 'practice', 'review', 'placement']),
  title: z.string(),
  completedAt: iso,
  questionCount: z.number().int(),
  accuracy: nullableNumber,
  xp: z.number().int(),
  hintsUsed: z.number().int().nullable(),
  lessonId: z.string().nullable(),
  assignmentId: uuid.nullable(),
  classroomName: z.string().nullable(),
});
export const activityHistorySchema = z.array(historyItemSchema);
export type HistoryItem = z.infer<typeof historyItemSchema>;

export const rewardsSchema = z.object({
  totalXp: z.number().int(),
  currentStreak: z.number().int(),
  longestStreak: z.number().int(),
  badges: z.array(z.object({ badgeId: z.string(), awardedAt: iso })),
  days: z.array(z.object({ date: isoDate, xp: z.number().int(), minutes: z.number().int(), sessions: z.number().int(), qualifying: z.boolean() })),
  today: isoDate,
});
export type Rewards = z.infer<typeof rewardsSchema>;

export const myClassSchema = z.object({
  classroomId: uuid,
  name: z.string(),
  description: z.string(),
  teacherName: z.string(),
  timezone: z.string(),
  leaderboardEnabled: z.boolean(),
  joinedAt: iso,
  archived: z.boolean(),
  openAssignments: z.number().int(),
});
export const myClassesSchema = z.array(myClassSchema);
export type MyClass = z.infer<typeof myClassSchema>;

export const joinResultSchema = z.union([
  z.object({ ok: z.literal(true), classroomId: uuid, name: z.string() }),
  z.object({ ok: z.literal(false), code: z.enum(['INVALID_CODE', 'ALREADY_MEMBER', 'OWN_CLASSROOM', 'REMOVED_FROM_CLASS']), classroomId: uuid.optional(), name: z.string().optional() }),
]);
export type JoinResult = z.infer<typeof joinResultSchema>;

export const leaderboardSchema = z.union([
  z.object({ enabled: z.literal(false), classroomName: z.string() }),
  z.object({
    enabled: z.literal(true),
    classroomName: z.string(),
    period: z.object({ weekStart: isoDate, startsAt: iso, endsAt: iso }),
    entries: z.array(z.object({ rank: z.number().int(), displayName: z.string(), points: z.number().int(), isMe: z.boolean() })),
    me: z.object({ optedOut: z.boolean(), points: z.number().int() }).nullable(),
  }),
]);
export type Leaderboard = z.infer<typeof leaderboardSchema>;

/** App-wide leaderboard by all-time XP. `name` is null unless that learner chose to show it. */
export const globalLeaderboardSchema = z.object({
  period: z.literal('all-time'),
  participants: z.number().int(),
  entries: z.array(z.object({ rank: z.number().int(), name: z.string().nullable(), xp: z.number().int(), isMe: z.boolean() })),
  me: z.object({ rank: z.number().int().nullable(), xp: z.number().int(), showName: z.boolean() }),
});
export type GlobalLeaderboard = z.infer<typeof globalLeaderboardSchema>;

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export const sessionKindSchema = z.enum(['lesson', 'assignment', 'practice', 'review', 'placement']);
export type SessionKind = z.infer<typeof sessionKindSchema>;

export const questionPromptSchema = z.object({
  clef: clefSchema,
  /** Present when the question must be shown on a grand staff (clef = the note's staff). */
  staff: z.literal('grand').optional(),
  pitch: pitchIdSchema,
  answerMode: answerModeSchema,
  accidentalChoices: z.array(accidentalSchema),
});
export type QuestionPrompt = z.infer<typeof questionPromptSchema>;

export const rhythmPhraseSchema = z.object({
  timeSignature: z.string(),
  measures: z.number().int(),
  events: z.array(z.object({ start: z.number().int(), duration: z.number().int(), rest: z.boolean() })),
  tuplets: z.array(z.object({ start: z.number().int(), duration: z.number().int() })).optional(),
});

/** A rhythm question: the phrase to tap and its tempo. */
export const rhythmPromptSchema = z.object({
  phrase: rhythmPhraseSchema,
  tempo: z.number().int(),
  grouping: z.array(z.number().int()).optional(),
  countUnit: z.enum(['beat', 'eighth']).optional(),
  mode: z.enum(['rhythm', 'strong-beats']).optional(),
});
export type RhythmPromptValue = z.infer<typeof rhythmPromptSchema>;

const sessionQuestionFields = {
  id: uuid,
  position: z.number().int(),
  stage: z.string().nullable(),
  attempts: z.number().int(),
  solved: z.boolean(),
  firstAttemptCorrect: z.boolean().nullable(),
  hintLevel: z.number().int(),
  revealed: z.boolean(),
  tried: z.array(z.string()).default([]),
};

export const sessionQuestionSchema = z.object({ ...sessionQuestionFields, prompt: questionPromptSchema });
export type SessionQuestion = z.infer<typeof sessionQuestionSchema>;

export const rhythmSessionQuestionSchema = z.object({ ...sessionQuestionFields, prompt: rhythmPromptSchema });
export type RhythmSessionQuestion = z.infer<typeof rhythmSessionQuestionSchema>;

export const finalizeResultSchema = z.object({
  sessionId: uuid,
  kind: sessionKindSchema,
  questionCount: z.number().int(),
  firstAttemptCorrect: z.number().int(),
  accuracy: z.number(),
  hintsUsed: z.number().int(),
  correctedCount: z.number().int(),
  xp: z.object({
    eligible: z.boolean(),
    base: z.number().int(),
    accuracy: z.number().int(),
    perfect: z.number().int(),
    multiplier: z.number(),
    /** The content was already mastered when the session started (repeat rule applies). Absent on v1 results. */
    contentMastered: z.boolean().optional(),
    /** A hint was used, so the session earned no XP (bonuses wait for a hint-free session). Absent before v3. */
    hintForfeit: z.boolean().optional(),
    session: z.number().int(),
    lessonBonus: z.number().int(),
    assignmentBonus: z.number().int(),
    total: z.number().int(),
  }),
  lesson: z
    .object({ lessonId: z.string(), passed: z.boolean(), firstCompletion: z.boolean(), passAccuracy: z.number(), milestone: z.string().nullable() })
    .nullable(),
  assignment: z
    .object({
      assignmentId: uuid,
      sessionsCompleted: z.number().int(),
      requiredSessions: z.number().int(),
      completed: z.boolean(),
      completedNow: z.boolean(),
      completedLate: z.boolean().nullable(),
      masteryTarget: nullableNumber,
      masteryMet: z.boolean().nullable(),
    })
    .nullable(),
  placement: z
    .object({
      startLessonId: z.string(),
      placedLessonIds: z.array(z.string()),
      stages: z.array(z.object({ stageId: z.string(), correct: z.number(), total: z.number(), passed: z.boolean() })),
    })
    .nullable(),
  streak: z.object({ current: z.number().int(), longest: z.number().int() }),
  totalXp: z.number().int(),
  badges: z.array(z.string()),
  leaderboardPoints: z.number().int(),
  skills: z.array(z.object({ skillId: z.string(), status: z.string(), correct: z.number().int(), total: z.number().int(), nextReviewOn: isoDate.nullable() })),
  alreadyFinalized: z.boolean(),
});
export type FinalizeResult = z.infer<typeof finalizeResultSchema>;

const sessionStateFields = {
  id: uuid,
  kind: sessionKindSchema,
  title: z.string(),
  status: z.enum(['active', 'completed', 'abandoned', 'expired']),
  lessonId: z.string().nullable(),
  assignmentId: uuid.nullable(),
  classroomId: uuid.nullable(),
  trackClef: trackKeySchema.nullable(),
  hintsAllowed: z.boolean(),
  accessibleMode: z.boolean(),
  startedAt: iso,
  expiresAt: iso,
  result: finalizeResultSchema.omit({ alreadyFinalized: true }).extend({ alreadyFinalized: z.boolean().optional() }).nullable(),
};

export const noteIdSessionStateSchema = z.object({
  ...sessionStateFields,
  exerciseType: z.literal('note-identification'),
  questions: z.array(sessionQuestionSchema),
});
export type NoteIdSessionState = z.infer<typeof noteIdSessionStateSchema>;

export const rhythmSessionStateSchema = z.object({
  ...sessionStateFields,
  exerciseType: z.literal('rhythm'),
  questions: z.array(rhythmSessionQuestionSchema),
});
export type RhythmSessionState = z.infer<typeof rhythmSessionStateSchema>;

export const sessionStateSchema = z.discriminatedUnion('exerciseType', [noteIdSessionStateSchema, rhythmSessionStateSchema]);
export type SessionState = z.infer<typeof sessionStateSchema>;

export const answerValueSchema = z.object({
  letter: z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G']),
  accidental: accidentalSchema,
  octave: z.number().int().min(0).max(8).optional(),
});
export type AnswerValue = z.infer<typeof answerValueSchema>;

/** A rhythm performance: tap times in whole ms from the phrase's first beat, latency-corrected. */
export const rhythmAnswerSchema = z.object({ taps: z.array(z.number().int()).max(200) });
export type RhythmAnswerValue = z.infer<typeof rhythmAnswerSchema>;

/** A queued client event. `id` is generated on the client and makes retries idempotent. */
export const answerEventSchema = z.object({
  id: uuid,
  sessionId: uuid,
  questionId: uuid,
  kind: z.enum(['answer', 'hint', 'reveal']),
  answer: z.union([answerValueSchema, rhythmAnswerSchema]).nullable(),
  hintLevel: z.number().int().min(1).max(2).nullable(),
  responseMs: z.number().int().min(0).max(3_600_000).nullable(),
  clientCreatedAt: iso,
});
export type AnswerEvent = z.infer<typeof answerEventSchema>;

export const answerEventResultSchema = z.object({
  eventId: uuid,
  kind: z.enum(['answer', 'hint', 'reveal']),
  attemptNo: z.number().int().nullable(),
  correct: z.boolean().nullable(),
  code: z.string().nullable(),
  duplicate: z.boolean(),
});
export type AnswerEventResult = z.infer<typeof answerEventResultSchema>;

/** Edge Function `session-start` request. */
export const sessionStartRequestSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('lesson'), lessonId: z.string().min(1).max(80), accessibleMode: z.boolean().optional() }),
  z.object({ kind: z.literal('assignment'), assignmentId: uuid, accessibleMode: z.boolean().optional() }),
  z.object({ kind: z.literal('review'), accessibleMode: z.boolean().optional() }),
  /** `path: 'rhythm'` is the rhythm placement check; otherwise the note-reading check on the active clef. */
  z.object({ kind: z.literal('placement'), path: z.enum(['note-reading', 'rhythm']).optional(), accessibleMode: z.boolean().optional() }),
  z.object({
    kind: z.literal('practice'),
    /** Defaults to note identification. */
    exerciseType: exerciseTypeSchema.optional(),
    title: z.string().trim().max(80).optional(),
    config: z.unknown(),
    accessibleMode: z.boolean().optional(),
  }),
]);
export type SessionStartRequest = z.infer<typeof sessionStartRequestSchema>;

export const sessionStartResponseSchema = z.object({ sessionId: uuid, resumed: z.boolean() });
export type SessionStartResponse = z.infer<typeof sessionStartResponseSchema>;

// ---------------------------------------------------------------------------
// Teacher
// ---------------------------------------------------------------------------

export const inviteCodeSchema = z.object({ code: z.string(), expiresAt: iso.nullable(), expired: z.boolean(), createdAt: iso.optional() });

export const teacherClassroomSummarySchema = z.object({
  id: uuid,
  name: z.string(),
  description: z.string(),
  timezone: z.string(),
  leaderboardEnabled: z.boolean(),
  archivedAt: iso.nullable(),
  createdAt: iso,
  memberCount: z.number().int(),
  inviteCode: inviteCodeSchema.nullable(),
  activeAssignments: z.number().int(),
  dueSoon: z.number().int(),
  draftPlans: z.number().int(),
});
export const teacherDashboardSchema = z.array(teacherClassroomSummarySchema);
export type TeacherClassroomSummary = z.infer<typeof teacherClassroomSummarySchema>;

export const createClassroomResultSchema = z.object({ classroomId: uuid, inviteCode: z.string() });

export const rosterEntrySchema = z.object({
  studentId: uuid,
  displayName: z.string(),
  status: z.enum(['active', 'left', 'removed']),
  joinedAt: iso,
  endedAt: iso.nullable(),
  assigned: z.number().int(),
  completed: z.number().int(),
  firstAttemptAccuracy: nullableNumber,
  lastActivityAt: iso.nullable(),
});
export type RosterEntry = z.infer<typeof rosterEntrySchema>;

export const planSummarySchema = z.object({
  id: uuid,
  title: z.string(),
  weekStart: isoDate,
  status: z.enum(['draft', 'published', 'archived']),
  currentVersion: z.number().int(),
  hasUnpublishedChanges: z.boolean(),
  activityCount: z.number().int(),
  updatedAt: iso,
});
export type PlanSummary = z.infer<typeof planSummarySchema>;

export const classroomDetailSchema = z.object({
  classroom: z.object({
    id: uuid,
    name: z.string(),
    description: z.string(),
    timezone: z.string(),
    leaderboardEnabled: z.boolean(),
    archivedAt: iso.nullable(),
    createdAt: iso,
  }),
  inviteCode: inviteCodeSchema.nullable(),
  roster: z.array(rosterEntrySchema),
  plans: z.array(planSummarySchema),
});
export type ClassroomDetail = z.infer<typeof classroomDetailSchema>;

export const planActivitySchema = z.object({
  id: uuid,
  activityKey: uuid,
  position: z.number().int(),
  title: z.string(),
  exerciseType: z.string(),
  config: z.record(z.string(), z.unknown()),
  requiredSessions: z.number().int(),
  masteryTarget: nullableNumber,
  audience: z.enum(['class', 'selected']),
  selectedStudentIds: z.array(uuid),
  releaseAt: iso,
  dueAt: iso.nullable(),
});
export type PlanActivity = z.infer<typeof planActivitySchema>;

export const planAssignmentSchema = z.object({
  id: uuid,
  activityKey: uuid,
  version: z.number().int(),
  title: z.string(),
  exerciseType: exerciseTypeSchema.default('note-identification'),
  status: z.enum(['active', 'superseded', 'archived']),
  releaseAt: iso,
  dueAt: iso.nullable(),
  requiredSessions: z.number().int(),
  masteryTarget: nullableNumber,
  createdAt: iso,
  supersededBy: uuid.nullable(),
  recipients: z.number().int(),
  completed: z.number().int(),
  newMembersNotAssigned: z.number().int(),
});
export type PlanAssignment = z.infer<typeof planAssignmentSchema>;

export const planDetailSchema = z.object({
  plan: z.object({
    id: uuid,
    classroomId: uuid,
    title: z.string(),
    weekStart: isoDate,
    status: z.enum(['draft', 'published', 'archived']),
    assignmentFocused: z.boolean(),
    revision: z.number().int(),
    publishedRevision: z.number().int().nullable(),
    currentVersion: z.number().int(),
    hasUnpublishedChanges: z.boolean(),
    updatedAt: iso,
  }),
  classroom: z.object({ id: uuid, name: z.string(), timezone: z.string(), archivedAt: iso.nullable() }),
  activities: z.array(planActivitySchema),
  assignments: z.array(planAssignmentSchema),
  versions: z.array(z.object({ version: z.number().int(), publishedAt: iso })),
  members: z.array(z.object({ studentId: uuid, displayName: z.string() })),
});
export type PlanDetail = z.infer<typeof planDetailSchema>;

const activityInputFields = {
  id: uuid.optional(),
  title: z.string().trim().min(1, 'Give the activity a title.').max(80),
  requiredSessions: z.number().int().min(1).max(10),
  masteryTarget: z.number().min(0.5).max(1).nullable(),
  audience: z.enum(['class', 'selected']),
  selectedStudentIds: z.array(uuid),
  releaseAt: iso,
  dueAt: iso.nullable(),
};

export const activityInputSchema = z.discriminatedUnion('exerciseType', [
  z.object({ ...activityInputFields, exerciseType: z.literal('note-identification'), config: noteIdConfigSchema }),
  z.object({ ...activityInputFields, exerciseType: z.literal('rhythm'), config: rhythmConfigSchema }),
]);
export type ActivityInput = z.infer<typeof activityInputSchema>;

export const templateSchema = z.object({
  id: uuid,
  title: z.string(),
  description: z.string(),
  builtIn: z.boolean(),
  activities: z.array(z.record(z.string(), z.unknown())),
  createdAt: iso,
});
export const templatesSchema = z.array(templateSchema);
export type PlanTemplateSummary = z.infer<typeof templateSchema>;

export const publishRequestSchema = z.object({ planId: uuid });
export const publishResponseSchema = z.object({
  version: z.number().int(),
  created: z.number().int(),
  replaced: z.number().int(),
  unchanged: z.number().int(),
  archived: z.number().int(),
});
export type PublishResponse = z.infer<typeof publishResponseSchema>;

export const activityValidationErrorSchema = z.object({
  activityId: uuid,
  title: z.string(),
  issues: z.array(z.object({ path: z.string(), code: z.string(), message: z.string() })),
});

export const skillPatternSchema = z.object({
  skillId: z.string(),
  total: z.number().int(),
  correct: z.number().int(),
  codes: z.record(z.string(), z.number().int()),
});
export type SkillPattern = z.infer<typeof skillPatternSchema>;

export const assignmentReportSchema = z.object({
  assignment: z.object({
    id: uuid,
    title: z.string(),
    version: z.number().int(),
    status: z.enum(['active', 'superseded', 'archived']),
    planId: uuid,
    classroomId: uuid,
    classroomName: z.string(),
    timezone: z.string(),
    exerciseType: exerciseTypeSchema.default('note-identification'),
    config: exerciseConfigSchema,
    requiredSessions: z.number().int(),
    masteryTarget: nullableNumber,
    releaseAt: iso,
    dueAt: iso.nullable(),
    supersededBy: uuid.nullable(),
  }),
  students: z.array(
    z.object({
      studentId: uuid,
      displayName: z.string(),
      membershipStatus: z.enum(['active', 'left', 'removed']),
      assignedAt: iso,
      source: z.enum(['publish', 'new_students']),
      sessionsCompleted: z.number().int(),
      completedAt: iso.nullable(),
      completedLate: z.boolean().nullable(),
      questionTotal: z.number().int(),
      firstAttemptCorrect: z.number().int(),
      firstAttemptAccuracy: nullableNumber,
      hintsUsed: z.number().int(),
      masteryMetAt: iso.nullable(),
      lastActivityAt: iso.nullable(),
      errorPatterns: z.array(skillPatternSchema),
    }),
  ),
  skills: z.array(skillPatternSchema),
  recentActivity: z.array(z.object({ displayName: z.string(), completedAt: iso, accuracy: nullableNumber, questionCount: z.number().int() })),
});
export type AssignmentReport = z.infer<typeof assignmentReportSchema>;

export const studentReportSchema = z.object({
  student: z.object({ studentId: uuid, displayName: z.string(), status: z.enum(['active', 'left', 'removed']), joinedAt: iso, endedAt: iso.nullable() }),
  classroom: z.object({ id: uuid, name: z.string(), timezone: z.string() }),
  assignments: z.array(
    z.object({
      assignmentId: uuid,
      title: z.string(),
      version: z.number().int(),
      exerciseType: exerciseTypeSchema.default('note-identification'),
      status: z.enum(['active', 'superseded', 'archived']),
      dueAt: iso.nullable(),
      requiredSessions: z.number().int(),
      masteryTarget: nullableNumber,
      sessionsCompleted: z.number().int(),
      completedAt: iso.nullable(),
      completedLate: z.boolean().nullable(),
      firstAttemptAccuracy: nullableNumber,
      hintsUsed: z.number().int(),
      masteryMetAt: iso.nullable(),
      lastActivityAt: iso.nullable(),
    }),
  ),
  skills: z.array(z.object({ skillId: z.string(), qualifying: z.number().int(), correct: z.number().int() })),
  errorPatterns: z.array(skillPatternSchema),
  recentActivity: z.array(
    z.object({ title: z.string(), completedAt: iso, accuracy: nullableNumber, questionCount: z.number().int(), hintsUsed: z.number().int().nullable() }),
  ),
});
export type StudentReport = z.infer<typeof studentReportSchema>;

export const exportRowSchema = z.object({
  student: z.string(),
  assignment: z.string(),
  exerciseType: exerciseTypeSchema.default('note-identification'),
  version: z.number().int(),
  assignmentStatus: z.string(),
  dueAt: iso.nullable(),
  requiredSessions: z.number().int(),
  sessionsCompleted: z.number().int(),
  completedAt: iso.nullable(),
  completedLate: z.boolean().nullable(),
  firstAttemptAccuracy: nullableNumber,
  hintsUsed: z.number().int(),
  masteryTarget: nullableNumber,
  masteryMet: z.boolean().nullable(),
  lastActivityAt: iso.nullable(),
});
export const exportRowsSchema = z.array(exportRowSchema);
export type ExportRow = z.infer<typeof exportRowSchema>;
